/**
 * Rooms Browser - Room별 메시지 수 및 상태 확인
 * 
 * 비용 최적화:
 * - Room 목록: 페이지당 20개씩 로드
 * - 메시지 count: 선택된 Room만 getCountFromServer로 조회
 */

import { Trans, t } from '@lingui/macro';
import { MessageSquare, Users, Clock, RefreshCw, Loader2, ChevronRight, Search, Building2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Fire } from '../../services/FireClient';
import {
    collection, getDocs, query, where, orderBy, limit,
    startAfter, getCountFromServer, doc, getDoc, DocumentSnapshot
} from 'firebase/firestore';
import { IconHash } from '../components/FigmaIcons';

interface RoomData {
    id: string;
    name: string;
    workspaceId: string;
    workspaceName?: string;
    userCount: number;
    messageCount?: number;  // lazy load
    lastMessageAt?: Date;
    createdAt?: Date;
}

const PAGE_SIZE = 20;

const DevAdminRooms: React.FC = () => {
    const [rooms, setRooms] = useState<RoomData[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRoom, setSelectedRoom] = useState<RoomData | null>(null);
    const [loadingCount, setLoadingCount] = useState(false);

    useEffect(() => {
        loadRooms();
    }, []);

    const loadRooms = async (loadMore = false) => {
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setRooms([]);
            setLastDoc(null);
        }

        try {
            const store = Fire.store;

            let q = query(
                collection(store, 'rooms'),
                orderBy('created', 'desc'),
                limit(PAGE_SIZE)
            );

            if (loadMore && lastDoc) {
                q = query(
                    collection(store, 'rooms'),
                    orderBy('created', 'desc'),
                    startAfter(lastDoc),
                    limit(PAGE_SIZE)
                );
            }

            const snap = await getDocs(q);

            // Workspace 이름 캐시
            const wsCache: Record<string, string> = {};

            const newRooms: RoomData[] = await Promise.all(
                snap.docs.map(async (roomDoc) => {
                    const data = roomDoc.data();

                    // Workspace 이름 조회 (캐싱)
                    let workspaceName = data.workspaceId ? wsCache[data.workspaceId] : undefined;
                    if (!workspaceName && data.workspaceId && data.workspaceId.length > 0) {
                        try {
                            const wsDoc = await getDoc(doc(store, 'workspaces', data.workspaceId));
                            workspaceName = wsDoc.data()?.name || 'Unknown';
                            wsCache[data.workspaceId] = workspaceName ?? 'Unknown';
                        } catch {
                            workspaceName = 'Unknown';
                        }
                    }

                    return {
                        id: roomDoc.id,
                        name: data.name || 'Unnamed Room',
                        workspaceId: data.workspaceId || '',
                        workspaceName,
                        userCount: data.userIds?.length || 0,
                        lastMessageAt: data.lastMessageAt?.toDate?.(),
                        createdAt: data.createdAt?.toDate?.(),
                    };
                })
            );

            if (loadMore) {
                setRooms(prev => [...prev, ...newRooms]);
            } else {
                setRooms(newRooms);
            }

            setLastDoc(snap.docs[snap.docs.length - 1] || null);
            setHasMore(snap.docs.length === PAGE_SIZE);
        } catch (error) {
            console.error('Failed to load rooms:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // 선택된 Room의 메시지 count 로드 (lazy)
    const loadMessageCount = async (room: RoomData) => {
        setSelectedRoom(room);

        if (room.messageCount !== undefined) return; // 이미 로드됨

        setLoadingCount(true);
        try {
            const store = Fire.store;
            const countSnap = await getCountFromServer(
                query(collection(store, 'messages'), where('roomId', '==', room.id))
            );

            // Update room with message count
            setRooms(prev => prev.map(r =>
                r.id === room.id
                    ? { ...r, messageCount: countSnap.data().count }
                    : r
            ));
            setSelectedRoom(prev => prev ? { ...prev, messageCount: countSnap.data().count } : null);
        } catch (error) {
            console.error('Failed to count messages:', error);
        } finally {
            setLoadingCount(false);
        }
    };

    const formatDate = (date?: Date) => {
        if (!date) return '-';
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
        return date.toLocaleDateString();
    };

    const filteredRooms = rooms.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.workspaceName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex h-full">
            {/* Rooms List */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-semibold text-slate-900">
                            <Trans>Channels</Trans>
                        </h2>
                        <Badge variant="secondary">{rooms.length}</Badge>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder={t`Search channels...`}
                            className="pl-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <>
                            <div className="divide-y divide-slate-100">
                                {filteredRooms.map((room) => (
                                    <button
                                        key={room.id}
                                        onClick={() => loadMessageCount(room)}
                                        className={`w-full p-3 text-left hover:bg-slate-50 transition-colors ${selectedRoom?.id === room.id ? 'bg-emerald-50' : ''
                                            }`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <MessageSquare className="w-4 h-4 text-slate-400" />
                                            <span className="font-medium text-slate-900 truncate flex-1">
                                                {room.name}
                                            </span>
                                            {room.messageCount !== undefined && (
                                                <Badge variant="secondary" className="text-xs">
                                                    {room.messageCount}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                            <Building2 className="w-3 h-3" />
                                            <span className="truncate">{room.workspaceName || 'N/A'}</span>
                                            <span className="text-slate-300">|</span>
                                            <Users className="w-3 h-3" />
                                            <span>{room.userCount}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {hasMore && (
                                <div className="p-3">
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={() => loadRooms(true)}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trans>Load More</Trans>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Room Detail */}
            <div className="flex-1 bg-slate-50 p-6">
                {selectedRoom ? (
                    <div className="max-w-2xl">
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-6">
                                    <div>
                                        <h2 className="text-xl font-bold text-slate-900">
                                            {selectedRoom.name}
                                        </h2>
                                        <p className="text-sm text-slate-500 font-mono mt-1">
                                            {selectedRoom.id}
                                        </p>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => loadMessageCount(selectedRoom)}>
                                        <RefreshCw className="w-4 h-4" />
                                    </Button>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-slate-100 rounded-lg">
                                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                            <IconHash className="w-4 h-4" />
                                            <Trans>Messages</Trans>
                                        </div>
                                        <div className="text-2xl font-bold text-slate-900">
                                            {loadingCount ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                selectedRoom.messageCount?.toLocaleString() ?? '...'
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-4 bg-slate-100 rounded-lg">
                                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                            <Users className="w-4 h-4" />
                                            <Trans>Members</Trans>
                                        </div>
                                        <div className="text-2xl font-bold text-slate-900">
                                            {selectedRoom.userCount}
                                        </div>
                                    </div>

                                    <div className="p-4 bg-slate-100 rounded-lg">
                                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                            <Building2 className="w-4 h-4" />
                                            <Trans>Workspace</Trans>
                                        </div>
                                        <div className="text-sm font-medium text-slate-900 truncate">
                                            {selectedRoom.workspaceName || 'N/A'}
                                        </div>
                                    </div>

                                    <div className="p-4 bg-slate-100 rounded-lg">
                                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                            <Clock className="w-4 h-4" />
                                            <Trans>Last Activity</Trans>
                                        </div>
                                        <div className="text-sm font-medium text-slate-900">
                                            {formatDate(selectedRoom.lastMessageAt)}
                                        </div>
                                    </div>
                                </div>

                                {/* Cold Room Warning */}
                                {selectedRoom.lastMessageAt &&
                                    (new Date().getTime() - selectedRoom.lastMessageAt.getTime()) > 180 * 24 * 60 * 60 * 1000 && (
                                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                                            ⚠️ <Trans>This channel has been inactive for over 180 days.</Trans>
                                        </div>
                                    )}
                            </CardContent>
                        </Card>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                        <div className="text-center">
                            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p><Trans>Select a channel to view details</Trans></p>
                            <p className="text-xs mt-1">
                                <Trans>Message count loads on selection</Trans>
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminRooms;
